import { useCallback, useEffect, useRef, useState } from 'react';
import { myAreaShopOBS, setErrorManagerCode } from '@msc-b2c/shared-utils';
import { DiningFlowEntryPoint } from '@msc-b2c/interfaces/enums';
import { OBSI } from '@msc-b2c/interfaces/interfaces';
import {
  UsePaxEditBootstrapOverrideParams,
  UsePaxEditBootstrapOverrideResult,
} from '@msc-b2c/interfaces/interfaces';
import { getShopOBSData } from '../../../../../utils/myAreaUtils';
import { useDispatch } from 'react-redux';

/**
 * Handles INT135 prefetch state for CART pax-edit flow.
 *
 * Flow intent:
 * - First call starts INT135 request in background.
 * - Concurrent calls reuse the same promise (de-duplication).
 * - When card/entry point changes, cached override and in-flight promise are reset.
 */
export const usePaxEditBootstrapOverride = ({
  entryPoint,
  alreadyPurchasedObsCard,
  onDispatchAction,
}: UsePaxEditBootstrapOverrideParams): UsePaxEditBootstrapOverrideResult => {
  const [paxEditBootstrapOverride, setPaxEditBootstrapOverride] = useState<OBSI | null>(null);
  const paxEditFetchPromiseRef = useRef<Promise<void> | null>(null);
  const dispatch = useDispatch();

  /**
   * Reset cached override when product context changes.
   */
  useEffect(() => {
    setPaxEditBootstrapOverride(null);
    paxEditFetchPromiseRef.current = null;
  }, [alreadyPurchasedObsCard?.code, entryPoint]);

  /**
   * Prefetches INT135 metadata once and shares the same promise across callers.
   * This keeps button-click prefetch and confirm-step await in sync without
   * firing duplicate requests.
   */
  const prefetchINT135DiningRoomMetadata = useCallback(async () => {
    if (entryPoint !== DiningFlowEntryPoint.CART || !alreadyPurchasedObsCard) {
      return;
    }

    if (paxEditBootstrapOverride) {
      return;
    }

    if (paxEditFetchPromiseRef.current) {
      return paxEditFetchPromiseRef.current;
    }

    const fetchPromise = (async () => {
      try {
        const int135Data = await getShopOBSData(alreadyPurchasedObsCard);
        if (int135Data && (int135Data as any).errorId === undefined) {
          onDispatchAction(myAreaShopOBS(int135Data));
          setPaxEditBootstrapOverride(int135Data);
        } else {
          dispatch(setErrorManagerCode((int135Data as any).code ?? 'INT135_FETCH_ERROR'));
        }
      } catch {
        // Keep INT130 fallback when prefetch fails.
      } finally {
        paxEditFetchPromiseRef.current = null;
      }
    })();

    paxEditFetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [alreadyPurchasedObsCard, entryPoint, onDispatchAction, paxEditBootstrapOverride]);

  return {
    paxEditBootstrapOverride,
    prefetchINT135DiningRoomMetadata,
  };
};
